import { useState } from "react";

import { PERMISSION_KEY, TRANSLATE_PREFIX } from "../..";
import {
  getAllContractSweepRecipientGroup,
  getExportContractSweepRecipientGroup,
} from "../../../../../api/merchant";
import { Ipage } from "../../../../../api/types";
import GeneralBtn from "../../../../../components/GeneralBtn";
import { Box, Container } from "../../../../../components/MuiGenerals";
import TableTab from "../../../../../components/TableTab";
import { useAlerting, usePermission } from "../../../../../hooks";
import { selectCrossMerchant } from "../../../../../reducer/crossMerchantSlice";
import { useAppSelector } from "../../../../../reducer/hooks";
import {
  displayAmount,
  downloadFiles,
  findChainInfo,
  findDecimalByChainNameAndAsset,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../../../utils/helper";
import { DivideLine } from "../../../../../utils/HelperComp";
import { customSx } from "../../../../../utils/styling";
import { createZusInstance } from "../../../../../zustand/store";
import { Filter } from "./components/Filter";
import Table from "./components/Table";
import useTabTranslation from "src/hooks/useTabTranslation";
import {
  AutoSweepGroupRotationReason,
  SweepDestGroupType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import { GetAllRes } from "./types/GetAllRes";
import cleanParams from "../../helpers/cleanParams";
import { EXPORT_FILE_NAME, TAB_TRANSLTE_PREFIX } from "./constants";
import { ApiParams } from "./types/ApiParams";

export const initZusParams = {
  page: 0,
  pageSize: 20,
} as const;

export const useZusParams = createZusInstance<Ipage & Partial<ApiParams>>(
  initZusParams,
);

export const apis = {
  getAll: getAllContractSweepRecipientGroup,
  export: getExportContractSweepRecipientGroup,
};

function ContractSweepRecipientGroupTab() {
  const zusParams = useZusParams();
  const { hasPermission } = usePermission();
  const { alerting } = useAlerting();
  const [count, setCount] = useState(0);
  const { merchantMapping } = useAppSelector(selectCrossMerchant);

  const { t, tc, translate } = useTabTranslation({
    featurePrefix: TRANSLATE_PREFIX,
    tabPrefix: TAB_TRANSLTE_PREFIX,
  });

  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      apis.export(
        { ...cleanParams(zusParams.body), page, pageSize },
        { signal },
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const omitKeys = ["rawData"];

    const res = listMapping("name", rawRes, {
      t,
      translate,
      omitKeys,
    });
    const config = {};
    downloadFiles(EXPORT_FILE_NAME, res, config);
  };

  const isExportBtnShown = hasPermission(
    PERMISSION_KEY.TabContractSweepRecipientGroup.Export,
  );

  return (
    <Box
      sx={{
        backgroundColor: "white",
        paddingTop: "2rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
      }}
    >
      <Container disableGutters maxWidth={false}>
        <Filter />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Box sx={customSx.tableExportBar}>
          {isExportBtnShown && <GeneralBtn label="export" onClick={onExport} />}
        </Box>
        <TableTab>
          <Table {...{ listMapping, setCount }} />
        </TableTab>
      </Container>
    </Box>
  );
}

const listMapping = (
  which: "key" | "name",
  array: GetAllRes[],
  config?: {
    omitKeys: string[];
    t: any;
    translate: any;
  },
): any[][] => {
  const { omitKeys = [], t, translate } = config || {};

  const res = array.map((item) => {
    const chainInfo = findChainInfo(String(item.chain_type), item.chain_id);
    const decimals = findDecimalByChainNameAndAsset(
      String(item.chain_type),
      String(item.chain_id),
      String(item.asset_name),
    );

    const getGroupType = (type: SweepDestGroupType) => {
      if (!SweepDestGroupType[type]) {
        return "";
      }

      return translate(`enumSweepDestGroupType.${SweepDestGroupType[type]}`);
    };
    const getRotationReason = (reason: AutoSweepGroupRotationReason) => {
      if (!AutoSweepGroupRotationReason[reason]) {
        return "";
      }

      return translate(
        `enumAutoSweepGroupRotationReason.${AutoSweepGroupRotationReason[reason]}`,
      );
    };

    const mappedResult = [
      ["rawData", "rawData", item],
      ["groupName", t("groupName"), item.group_name],
      ["groupType", t("groupType"), getGroupType(item.group_type)],
      ["groupSequence", t("groupSequence"), item.group_seq],
      ["chainName", t("chainName"), chainInfo?.name],
      ["currency", t("currency"), item.asset_name],
      [
        "highRiskAmount",
        t("highRiskAmount"),
        displayAmount(item.high_risk_balance, decimals),
      ],

      [
        "lowRiskAmount",
        t("lowRiskAmount"),
        displayAmount(item.low_risk_balance, decimals),
      ],
      [
        "totalAmount",
        t("totalAmount"),
        displayAmount(item.total_balance, decimals),
      ],
      [
        "activeStartTime",
        t("activeStartTime"),
        toDisplayTime(item.enabled_time),
      ],
      ["activeEndTime", t("activeEndTime"), toDisplayTime(item.disabled_time)],
      [
        "rotationReason",
        t("rotationReason"),
        getRotationReason(item.rotation_reason),
      ],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });

  const output = res.map(listMappingTransform(which));
  return output;
};

export default ContractSweepRecipientGroupTab;
